import React, { useState, useEffect, useRef } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../apis/fb";
import "../components/css/style.css";

const ReviewsForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    organization: "",
    email: "",
    service: "",
    rating: "",
    feedback: "",
    suggestions: ""
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });
  const [isVisible, setIsVisible] = useState(false);
  const formRef = useRef(null);

  const ratings = [
    { label: "Poor", value: 1, emoji: "😞" },
    { label: "Fair", value: 2, emoji: "😐" },
    { label: "Good", value: 3, emoji: "🙂" },
    { label: "Very Good", value: 4, emoji: "😊" },
    { label: "Excellent", value: 5, emoji: "🤩" },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (formRef.current) {
      observer.observe(formRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingClick = (value) => {
    setFormData(prev => ({
      ...prev,
      rating: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: "", message: "" });

    // Validate required fields
    if (!formData.name || !formData.email || !formData.service || !formData.rating || !formData.feedback) {
      setSubmitStatus({
        type: "error",
        message: "Please fill in all required fields (Name, Email, Service, Rating, and Feedback)"
      });
      setIsSubmitting(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({
        type: "error",
        message: "Please enter a valid email address"
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Add document
      const docRef = await addDoc(collection(db, "reviews"), {
        ...formData,
        rating: parseInt(formData.rating),
        createdAt: serverTimestamp(),
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });

      console.log("Success", docRef);
      
      setSubmitStatus({
        type: "success",
        message: "Thank you for your feedback! Your review has been submitted successfully."
      });

      // Reset form
      setFormData({
        name: "",
        organization: "",
        email: "",
        service: "",
        rating: "",
        feedback: "",
        suggestions: ""
      });

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus({ type: "", message: "" });
      }, 5000);

    } catch (error) {
      console.error("Error adding document: ", error);
      setSubmitStatus({
        type: "error",
        message: "Failed to submit review. Please try again later."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="feedback-wrapper">
      <div className="blob-container">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
        <div className="blob blob-3"></div>
        <div className="blob blob-4"></div>
      </div>

      <div 
        ref={formRef}
        className={`feedback-form-container ${isVisible ? 'animate-in' : ''}`}
      >
        <h2 className="feedback-title">
          <span className="title-gradient">Your Voice Matters</span>
        </h2>
        <p className="feedback-subtitle">
          We appreciate your time in providing review. Your input helps us improve
          our services and deliver better experiences to all our clients.
        </p>

        {submitStatus.message && (
          <div className={`submit-status ${submitStatus.type}`}>
            {submitStatus.message}
          </div>
        )}

        <form className="feedback-form" onSubmit={handleSubmit}>
          <div className="form-group slide-up" style={{animationDelay: '0.1s'}}>
            <label className="feedback-label">
              Your Name <span className="required">*</span>
            </label>
            <input 
              className="feedback-input" 
              type="text" 
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter your name"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group slide-up" style={{animationDelay: '0.2s'}}>
            <label className="feedback-label">Your Organization</label>
            <input 
              className="feedback-input" 
              type="text" 
              name="organization"
              value={formData.organization}
              onChange={handleInputChange}
              placeholder="Enter your organization name"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group slide-up" style={{animationDelay: '0.3s'}}>
            <label className="feedback-label">
              Email Address <span className="required">*</span>
            </label>
            <input 
              className="feedback-input" 
              type="email" 
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="your.email@example.com"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group slide-up" style={{animationDelay: '0.4s'}}>
            <label className="feedback-label">
              Service Used <span className="required">*</span>
            </label>
            <select 
              className="feedback-select"
              name="service"
              value={formData.service}
              onChange={handleInputChange}
              required
              disabled={isSubmitting}
            >
              <option value="">Select a service</option>
              <option value="Website Development">Website Development</option>
              <option value="Mobile App Development">Mobile App Development</option>
              <option value="Complete Brand Identity">Complete Brand Identity</option>
              <option value="Performance Marketing & Growth Acceleration">Performance Marketing & Growth Acceleration</option>
            </select>
          </div>

          <div className="form-group slide-up" style={{animationDelay: '0.5s'}}>
            <label className="feedback-label">
              How would you rate your experience? <span className="required">*</span>
            </label>
            <div className="feedback-rating">
              {ratings.map((r) => (
                <div
                  key={r.value}
                  className={`feedback-rating-item ${
                    formData.rating === r.value ? "active" : ""
                  } ${isSubmitting ? 'disabled' : ''}`}
                  onClick={() => !isSubmitting && handleRatingClick(r.value)}
                >
                  <span className="feedback-emoji">{r.emoji}</span>
                  <p className="feedback-rating-text">{r.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group slide-up" style={{animationDelay: '0.6s'}}>
            <label className="feedback-label">
              Your Feedback <span className="required">*</span>
            </label>
            <textarea
              className="feedback-textarea"
              name="feedback"
              value={formData.feedback}
              onChange={handleInputChange}
              placeholder="Please share your experience with us..."
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group slide-up" style={{animationDelay: '0.7s'}}>
            <label className="feedback-label">Suggestions for Improvement</label>
            <textarea
              className="feedback-textarea"
              name="suggestions"
              value={formData.suggestions}
              onChange={handleInputChange}
              placeholder="How can we improve our services? (Optional)"
              disabled={isSubmitting}
            />
          </div>

          <button 
            className={`feedback-button pulse ${isSubmitting ? 'submitting' : ''}`} 
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="button-text">Submitting...</span>
                <span className="spinner"></span>
              </>
            ) : (
              <>
                <span className="button-text">Submit Review</span>
                <span className="button-icon">→</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ReviewsForm;